import Layout from '../../layout/index.vue'
import { constantRoutes } from '../../router'

const state = {
  routes: [],
  addRoutes: []
}

const visibleConstantRoutes = () => constantRoutes.filter(r => !r.hidden)

const mutations = {
  SET_ROUTES: (state, routes) => {
    state.addRoutes = routes
    // 合并常量路由和动态路由，过滤掉登录等hidden路由
    state.routes = [...visibleConstantRoutes(), ...routes]
  },
  RESET_ROUTES: state => {
    state.addRoutes = []
    state.routes = [...visibleConstantRoutes()]
  }
}

const actions = {
  resetRoutes({ commit }) {
    commit('RESET_ROUTES')
  },
  // 根据角色动态生成路由
  async generateRoutes({ commit }, menus) {
    return new Promise(resolve => {
      const accessedRoutes = buildRoutesFromMenus(menus || [])
      commit('SET_ROUTES', accessedRoutes)
      resolve(accessedRoutes)
    })
  }
}

const isLayoutComponent = component =>
  component && component.toLowerCase() === 'layout/index'

const normalizePath = path =>
  String(path || '')
    .replace(/\\/g, '/')
    .replace(/\/+/g, '/')
    .trim()

const ensureLeadingSlash = path => {
  const value = normalizePath(path)
  return value.startsWith('/') ? value : `/${value}`
}

const stripLeadingSlash = path => normalizePath(path).replace(/^\/+/, '')

const buildTree = menus => {
  const nodes = menus
    // 仅排除按钮权限 C；目录 D、菜单 M 参与路由树
    .filter(menu => menu && menu.status !== 0 && menu.type !== 'C')
    .map(menu => ({ ...menu, children: [] }))
  const map = new Map(nodes.map(node => [node.id, node]))
  const roots = []
  nodes.forEach(node => {
    if (node.parentId && map.has(node.parentId)) {
      map.get(node.parentId).children.push(node)
    } else {
      roots.push(node)
    }
  })
  const sortByOrder = (a, b) => {
    if (a.orderNum == null && b.orderNum == null) return (a.id || 0) - (b.id || 0)
    if (a.orderNum == null) return 1
    if (b.orderNum == null) return -1
    if (a.orderNum !== b.orderNum) return a.orderNum - b.orderNum
    return (a.id || 0) - (b.id || 0)
  }
  const sortTree = list => {
    list.sort(sortByOrder)
    list.forEach(child => sortTree(child.children || []))
  }
  sortTree(roots)
  return roots
}

const loadView = component => {
  const view = normalizePath(component).replace(/^\//, '').replace(/\.vue$/, '')
  return () => import(`../../views/${view}.vue`)
}

const buildRedirect = route => {
  if (!route.children || route.children.length === 0) return undefined
  const firstChild = route.children[0]
  const childPath = stripLeadingSlash(firstChild.path)
  return childPath ? `${route.path}/${childPath}`.replace(/\/+/g, '/') : route.path
}

const normalizeChildPath = (childPath, parentPath) => {
  const child = ensureLeadingSlash(childPath)
  const parent = ensureLeadingSlash(parentPath)
  if (child === parent) return ''
  if (child.startsWith(`${parent}/`)) {
    return stripLeadingSlash(child.slice(parent.length + 1))
  }
  return stripLeadingSlash(child)
}

const buildRoutesFromMenus = menus => {
  const menuTree = buildTree(menus)
  return menuTree
    .map(menu => buildRoute(menu, true, ''))
    .filter(Boolean)
}

const buildRoute = (menu, isRoot, parentPath) => {
  const hasChildren = Array.isArray(menu.children) && menu.children.length > 0
  const isLayout = isLayoutComponent(menu.component)
  const fullPath = ensureLeadingSlash(menu.path)
  const meta = {
    title: menu.menuName,
    icon: menu.icon
  }
  const name = menu.menuCode || `Menu${menu.id}`

  if (isRoot && !hasChildren && !isLayout) {
    // 没有 component 时，生成仅导航用的路由（指向常量路由中已定义的页面）
    if (!menu.component) {
      return {
        path: fullPath,
        name,
        meta
      }
    }
    return {
      path: fullPath,
      component: Layout,
      children: [
        {
          path: '',
          name,
          component: loadView(menu.component),
          meta
        }
      ]
    }
  }

  if (isLayout || hasChildren) {
    const route = {
      path: isRoot ? fullPath : normalizeChildPath(fullPath, parentPath),
      component: Layout,
      meta,
      alwaysShow: true
    }
    if (hasChildren) {
      route.children = menu.children
        .map(child => buildRoute(child, false, fullPath))
        .filter(Boolean)
        .map(child => ({
          ...child,
          path: normalizeChildPath(child.path, route.path)
        }))
      route.redirect = buildRedirect({
        path: ensureLeadingSlash(route.path),
        children: route.children
      })
    }
    return route
  }

  // 没有 component 时，生成仅导航用的路由（指向常量路由中已定义的页面）
  if (!menu.component) {
    return {
      path: isRoot ? fullPath : normalizeChildPath(fullPath, parentPath),
      name,
      meta
    }
  }
  return {
    path: isRoot ? fullPath : normalizeChildPath(fullPath, parentPath),
    name,
    component: loadView(menu.component),
    meta
  }
}

export default {
  namespaced: true,
  state,
  mutations,
  actions
}